import { User, UserRole } from './models';
import { OWNIT_DEFAULT_THEME_ID } from './sample-data';
import { OwnitAdminPolicy, OwnitSupabaseConfig, getOwnitAdminPolicy, getOwnitCloudConfig } from './cloud-config';
import { OWNIT_DEFAULT_FONT_ID, normalizeOwnitFontId } from './fonts';

export interface OwnitAuthUser extends User {
    profileComplete: boolean;
}

export interface OwnitAuthSession {
    provider: 'mock-localStorage' | 'supabase';
    issuedAt: string;
    user: OwnitAuthUser;
    accessToken?: string;
}

interface StoredAuthUser extends OwnitAuthUser {
    passwordHash: string;
}

export interface SignInInput {
    identifier?: string;
    email?: string;
    password: string;
}

export interface SignUpInput {
    email: string;
    password: string;
    passwordConfirm: string;
    name: string;
    nickname: string;
}

export interface PasswordResetRequestInput {
    email: string;
}

export interface ProfileInput {
    name: string;
    nickname: string;
    profileImage: string;
    defaultFont: string;
    defaultTheme: string;
}

export interface AuthRepository {
    getSession(): OwnitAuthSession | null | Promise<OwnitAuthSession | null>;
    signIn(input: SignInInput): OwnitAuthSession | Promise<OwnitAuthSession>;
    signUp(input: SignUpInput): OwnitAuthSession | Promise<OwnitAuthSession>;
    requestPasswordReset(input: PasswordResetRequestInput): void | Promise<void>;
    updateProfile(input: ProfileInput): OwnitAuthSession | Promise<OwnitAuthSession>;
    signOut(): void | Promise<void>;
}

interface SupabaseAuthUser {
    id: string;
    email?: string;
    user_metadata?: Record<string, any>;
    created_at?: string;
    updated_at?: string;
}

interface SupabaseAuthPayload {
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    expires_at?: number;
    user?: SupabaseAuthUser;
}

interface StoredSupabaseSession extends SupabaseAuthPayload {
    issuedAt: string;
}

const SUPABASE_AUTH_SESSION_KEY = 'ownit.auth.supabase.session.v1';

export function createAuthRepository(): AuthRepository {
    const cloudConfig = getOwnitCloudConfig();
    const adminPolicy = getOwnitAdminPolicy(cloudConfig);
    const localFallback = new LocalStorageAuthRepository(adminPolicy);
    if (cloudConfig?.provider === 'supabase') {
        return new SupabaseAuthRepository(cloudConfig, localFallback, adminPolicy);
    }
    return localFallback;
}

export function getStoredSupabaseAccessToken(): string {
    try {
        const raw = globalThis.localStorage?.getItem(SUPABASE_AUTH_SESSION_KEY);
        if (!raw) return '';
        const parsed = JSON.parse(raw) as StoredSupabaseSession;
        return (parsed.access_token || '').trim();
    } catch (error) {
        return '';
    }
}

export class LocalStorageAuthRepository implements AuthRepository {
    private readonly usersKey = 'ownit.auth.users.v1';
    private readonly sessionKey = 'ownit.auth.session.v1';
    private readonly adminId = 'admin';
    private readonly adminUsername = 'admin';
    private readonly adminEmail = 'admin@ownit.local';

    constructor(private readonly adminPolicy: OwnitAdminPolicy = getOwnitAdminPolicy()) { }

    public getSession(): OwnitAuthSession | null {
        this.readAccounts();
        const session = this.readJson<{ userId: string; issuedAt: string } | null>(this.sessionKey, null);
        if (!session || !session.userId) return null;

        const account = this.findAccountById(session.userId);
        if (!account) {
            this.signOut();
            return null;
        }

        if (this.isAdminAccount(account) && !this.adminPolicy.defaultAdminEnabled) {
            this.signOut();
            return null;
        }

        return {
            provider: 'mock-localStorage',
            issuedAt: session.issuedAt,
            user: this.toPublicUser(account)
        };
    }

    public signIn(input: SignInInput): OwnitAuthSession {
        const identifier = this.normalizeLoginId(input.identifier || input.email || '');
        const password = input.password || '';
        if (!identifier || !password) throw new Error('아이디 또는 이메일과 비밀번호를 입력해 주세요.');

        const account = this.findAccountByIdentifier(identifier);
        if (account && this.isAdminAccount(account) && !this.adminPolicy.defaultAdminEnabled) {
            throw new Error(this.adminPolicy.message);
        }
        if (!account || account.passwordHash !== this.hashPassword(password)) {
            throw new Error('아이디 또는 이메일, 비밀번호가 올바르지 않습니다.');
        }

        return this.createSession(account.id);
    }

    public signUp(input: SignUpInput): OwnitAuthSession {
        const email = this.normalizeEmail(input.email);
        const password = input.password || '';
        const passwordConfirm = input.passwordConfirm || '';
        const name = (input.name || '').trim();
        const nickname = (input.nickname || '').trim();

        if (!email) throw new Error('이메일을 입력해 주세요.');
        if (!this.isEmail(email)) throw new Error('이메일 형식을 확인해 주세요.');
        if (password.length < 6) throw new Error('비밀번호는 6자 이상으로 입력해 주세요.');
        if (password !== passwordConfirm) throw new Error('비밀번호 확인이 일치하지 않습니다.');
        if (!name && !nickname) throw new Error('이름 또는 닉네임을 입력해 주세요.');
        if (this.findAccountByEmail(email)) throw new Error('이미 가입된 이메일입니다.');

        const now = new Date().toISOString();
        const account: StoredAuthUser = {
            id: this.createId(),
            username: email,
            email,
            authProvider: 'email',
            role: 'owner',
            passwordHash: this.hashPassword(password),
            name: name || nickname,
            nickname: nickname || name,
            profileImage: '',
            defaultFont: OWNIT_DEFAULT_FONT_ID,
            defaultTheme: OWNIT_DEFAULT_THEME_ID,
            profileComplete: false,
            createdAt: now,
            updatedAt: now
        };

        const accounts = this.readAccounts();
        accounts.push(account);
        this.writeAccounts(accounts);
        return this.createSession(account.id);
    }

    public requestPasswordReset(input: PasswordResetRequestInput): void {
        const email = this.normalizeEmail(input.email);
        if (!email) throw new Error('이메일을 입력해 주세요.');

        const account = this.findAccountByEmail(email);
        if (!account) throw new Error('가입된 이메일을 찾을 수 없습니다.');

        this.writeJson('ownit.auth.resetRequests.v1', {
            email,
            requestedAt: new Date().toISOString(),
            provider: 'mock-localStorage'
        });
    }

    public updateProfile(input: ProfileInput): OwnitAuthSession {
        const session = this.readJson<{ userId: string; issuedAt: string } | null>(this.sessionKey, null);
        if (!session || !session.userId) throw new Error('로그인 상태를 찾을 수 없습니다.');

        const accounts = this.readAccounts();
        const index = accounts.findIndex((account) => account.id === session.userId);
        if (index < 0) throw new Error('사용자 정보를 찾을 수 없습니다.');

        const name = (input.name || '').trim();
        const nickname = (input.nickname || '').trim();
        if (!name && !nickname) throw new Error('이름 또는 닉네임을 입력해 주세요.');

        accounts[index] = {
            ...accounts[index],
            name: name || nickname,
            nickname: nickname || name,
            profileImage: (input.profileImage || '').trim(),
            defaultFont: normalizeOwnitFontId(input.defaultFont),
            defaultTheme: input.defaultTheme || OWNIT_DEFAULT_THEME_ID,
            profileComplete: true,
            updatedAt: new Date().toISOString()
        };
        this.writeAccounts(accounts);
        return this.createSession(accounts[index].id);
    }

    public signOut(): void {
        this.storage.removeItem(this.sessionKey);
    }

    private createSession(userId: string): OwnitAuthSession {
        const account = this.findAccountById(userId);
        if (!account) throw new Error('사용자 정보를 찾을 수 없습니다.');

        const issuedAt = new Date().toISOString();
        this.writeJson(this.sessionKey, { userId, issuedAt });
        return {
            provider: 'mock-localStorage',
            issuedAt,
            user: this.toPublicUser(account)
        };
    }

    private readAccounts(): StoredAuthUser[] {
        const rawAccounts = this.readJson<Array<Partial<StoredAuthUser>>>(this.usersKey, []);
        const normalizedAccounts = rawAccounts.map((account) => this.normalizeStoredAccount(account));
        const seededAccounts = this.seedAdminUser(normalizedAccounts);
        if (JSON.stringify(rawAccounts) !== JSON.stringify(seededAccounts)) {
            this.writeJson(this.usersKey, seededAccounts);
        }
        return seededAccounts;
    }

    private writeAccounts(accounts: StoredAuthUser[]): void {
        this.writeJson(this.usersKey, accounts.map((account) => this.normalizeStoredAccount(account)));
    }

    private seedAdminUser(accounts: StoredAuthUser[]): StoredAuthUser[] {
        if (!this.adminPolicy.defaultAdminEnabled) return accounts;

        const index = accounts.findIndex((account) => this.isAdminAccount(account));
        const existing = index >= 0 ? accounts[index] : null;
        const admin = this.createAdminAccount(existing);

        if (index >= 0) {
            return accounts.map((account, accountIndex) => accountIndex === index ? admin : account);
        }

        return [admin, ...accounts];
    }

    private createAdminAccount(existing: StoredAuthUser | null): StoredAuthUser {
        const now = new Date().toISOString();
        return {
            id: this.adminId,
            username: this.adminUsername,
            email: this.adminEmail,
            authProvider: 'mock',
            role: 'admin',
            passwordHash: this.hashPassword(this.adminPolicy.defaultAdminPassword),
            name: existing?.name || '관리자',
            nickname: existing?.nickname || '관리자',
            profileImage: existing?.profileImage || '',
            defaultFont: normalizeOwnitFontId(existing?.defaultFont),
            defaultTheme: existing?.defaultTheme || OWNIT_DEFAULT_THEME_ID,
            profileComplete: true,
            createdAt: existing?.createdAt || now,
            updatedAt: existing?.updatedAt || existing?.createdAt || now
        };
    }

    private normalizeStoredAccount(account: Partial<StoredAuthUser>): StoredAuthUser {
        const now = new Date().toISOString();
        const id = (account.id || this.createId()).trim();
        const email = this.normalizeEmail(account.email || '');
        const username = this.normalizeUsername(account.username || email || id);
        const displayName = (account.name || account.nickname || username || email || '사용자').trim();

        return {
            id,
            username,
            email,
            authProvider: account.authProvider || 'email',
            role: this.normalizeRole(account.role),
            passwordHash: account.passwordHash || '',
            name: displayName,
            nickname: (account.nickname || displayName).trim(),
            profileImage: account.profileImage || '',
            defaultFont: normalizeOwnitFontId(account.defaultFont),
            defaultTheme: account.defaultTheme || OWNIT_DEFAULT_THEME_ID,
            profileComplete: Boolean(account.profileComplete),
            createdAt: account.createdAt || now,
            updatedAt: account.updatedAt || account.createdAt || now
        };
    }

    private findAccountById(userId: string): StoredAuthUser | null {
        return this.readAccounts().find((account) => account.id === userId) || null;
    }

    private findAccountByEmail(email: string): StoredAuthUser | null {
        const normalizedEmail = this.normalizeEmail(email);
        return this.readAccounts().find((account) => this.normalizeEmail(account.email) === normalizedEmail) || null;
    }

    private findAccountByIdentifier(identifier: string): StoredAuthUser | null {
        const normalizedIdentifier = this.normalizeLoginId(identifier);
        return this.readAccounts().find((account) => {
            return this.normalizeLoginId(account.id) === normalizedIdentifier
                || this.normalizeUsername(account.username) === normalizedIdentifier
                || this.normalizeEmail(account.email) === normalizedIdentifier;
        }) || null;
    }

    private toPublicUser(account: StoredAuthUser): OwnitAuthUser {
        const normalized = this.normalizeStoredAccount(account);
        return {
            id: normalized.id,
            username: normalized.username,
            email: normalized.email,
            authProvider: normalized.authProvider || 'email',
            role: normalized.role,
            name: normalized.name,
            nickname: normalized.nickname,
            profileImage: normalized.profileImage,
            defaultFont: normalized.defaultFont,
            defaultTheme: normalized.defaultTheme,
            profileComplete: normalized.profileComplete,
            createdAt: normalized.createdAt,
            updatedAt: normalized.updatedAt || normalized.createdAt
        };
    }

    private readJson<T>(key: string, fallback: T): T {
        const raw = this.storage.getItem(key);
        if (!raw) return fallback;
        try {
            return JSON.parse(raw) as T;
        } catch (error) {
            return fallback;
        }
    }

    private writeJson(key: string, value: any): void {
        this.storage.setItem(key, JSON.stringify(value));
    }

    private get storage(): Storage {
        if (typeof window === 'undefined' || !window.localStorage) {
            throw new Error('브라우저 저장소를 사용할 수 없습니다.');
        }
        return window.localStorage;
    }

    private isAdminAccount(account: Pick<StoredAuthUser, 'id' | 'username' | 'email' | 'role'>): boolean {
        return this.normalizeLoginId(account.id) === this.adminId
            || this.normalizeUsername(account.username) === this.adminUsername
            || this.normalizeEmail(account.email) === this.adminEmail
            || account.role === 'admin';
    }

    private normalizeEmail(email: string): string {
        return (email || '').trim().toLowerCase();
    }

    private normalizeUsername(username: string): string {
        return (username || '').trim().toLowerCase();
    }

    private normalizeLoginId(value: string): string {
        return (value || '').trim().toLowerCase();
    }

    private normalizeRole(role: UserRole | null | undefined): UserRole {
        if (role === 'admin' || role === 'member' || role === 'viewer') return role;
        return 'owner';
    }

    private isEmail(email: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    private hashPassword(password: string): string {
        let hash = 0;
        const source = `ownit-mock:${password}`;
        for (let index = 0; index < source.length; index++) {
            hash = ((hash << 5) - hash + source.charCodeAt(index)) | 0;
        }
        return `mock-${Math.abs(hash)}`;
    }

    private createId(): string {
        const random = Math.random().toString(36).slice(2, 10);
        return `user-${Date.now().toString(36)}-${random}`;
    }
}

export class OwnitMockAuthProvider extends LocalStorageAuthRepository { }

export class SupabaseAuthRepository implements AuthRepository {
    private readonly adminId = 'admin';
    private readonly adminEmail = 'admin@ownit.local';

    constructor(
        private readonly config: OwnitSupabaseConfig,
        private readonly localFallback: LocalStorageAuthRepository,
        private readonly adminPolicy: OwnitAdminPolicy = getOwnitAdminPolicy(config)
    ) { }

    public async getSession(): Promise<OwnitAuthSession | null> {
        const localSession = this.localFallback.getSession();
        if (localSession && this.isAdminUser(localSession.user)) {
            return this.adminPolicy.defaultAdminEnabled ? localSession : null;
        }

        const stored = this.readStoredSession();
        if (!stored?.access_token || !stored.user?.id) return localSession;

        const refreshed = await this.refreshIfNeeded(stored);
        if (!refreshed?.access_token || !refreshed.user?.id) return localSession;

        return this.toSession(refreshed);
    }

    public async signIn(input: SignInInput): Promise<OwnitAuthSession> {
        if (this.isAdminIdentifier(input.identifier || input.email || '')) {
            if (!this.adminPolicy.defaultAdminEnabled) {
                throw new Error(this.adminPolicy.message);
            }
            return this.localFallback.signIn(input);
        }

        try {
            const payload = await this.authRequest<SupabaseAuthPayload>('token?grant_type=password', {
                method: 'POST',
                body: {
                    email: (input.email || input.identifier || '').trim(),
                    password: input.password || ''
                }
            });
            return this.storeSession(payload);
        } catch (error) {
            const localSession = this.tryLocalSignIn(input);
            if (localSession) return localSession;
            throw error;
        }
    }

    public async signUp(input: SignUpInput): Promise<OwnitAuthSession> {
        const email = (input.email || '').trim().toLowerCase();
        if (!email) throw new Error('이메일을 입력해 주세요.');
        if ((input.password || '').length < 6) throw new Error('비밀번호는 6자 이상으로 입력해 주세요.');
        if (input.password !== input.passwordConfirm) throw new Error('비밀번호 확인이 일치하지 않습니다.');

        const payload = await this.authRequest<SupabaseAuthPayload>('signup', {
            method: 'POST',
            body: {
                email,
                password: input.password,
                data: {
                    name: (input.name || input.nickname || '').trim(),
                    nickname: (input.nickname || input.name || '').trim(),
                    default_font: OWNIT_DEFAULT_FONT_ID,
                    default_theme: OWNIT_DEFAULT_THEME_ID,
                    role: 'owner'
                }
            }
        });

        if (!payload.access_token) {
            throw new Error('회원가입 요청이 접수되었습니다. Supabase 이메일 인증을 완료한 뒤 로그인해 주세요.');
        }

        return this.storeSession(payload);
    }

    public async requestPasswordReset(input: PasswordResetRequestInput): Promise<void> {
        const email = (input.email || '').trim().toLowerCase();
        if (!email) throw new Error('이메일을 입력해 주세요.');
        await this.authRequest('recover', {
            method: 'POST',
            body: { email }
        });
    }

    public async updateProfile(input: ProfileInput): Promise<OwnitAuthSession> {
        const stored = this.readStoredSession();
        if (!stored?.access_token) {
            return this.localFallback.updateProfile(input);
        }

        const name = (input.name || input.nickname || '').trim();
        const nickname = (input.nickname || input.name || '').trim();
        if (!name && !nickname) throw new Error('이름 또는 닉네임을 입력해 주세요.');

        const user = await this.authRequest<SupabaseAuthUser>('user', {
            method: 'PUT',
            accessToken: stored.access_token,
            body: {
                data: {
                    name,
                    nickname,
                    profile_image: (input.profileImage || '').trim(),
                    default_font: normalizeOwnitFontId(input.defaultFont),
                    default_theme: input.defaultTheme || OWNIT_DEFAULT_THEME_ID,
                    profile_complete: true
                }
            }
        });
        const next = {
            ...stored,
            user,
            issuedAt: new Date().toISOString()
        };
        this.writeStoredSession(next);
        return this.toSession(next);
    }

    public async signOut(): Promise<void> {
        const stored = this.readStoredSession();
        if (stored?.access_token) {
            try {
                await this.authRequest('logout', {
                    method: 'POST',
                    accessToken: stored.access_token,
                    body: {}
                });
            } catch (error) {
                // Logout must clear the local session even when the network is unavailable.
            }
        }
        this.clearStoredSession();
        this.localFallback.signOut();
    }

    private async refreshIfNeeded(session: StoredSupabaseSession): Promise<StoredSupabaseSession | null> {
        const expiresAtMs = Number(session.expires_at || 0) * 1000;
        if (!session.refresh_token || !expiresAtMs || expiresAtMs - Date.now() > 60_000) {
            return session;
        }

        try {
            const refreshed = await this.authRequest<SupabaseAuthPayload>('token?grant_type=refresh_token', {
                method: 'POST',
                body: { refresh_token: session.refresh_token }
            });
            return this.storePayload(refreshed);
        } catch (error) {
            this.clearStoredSession();
            return null;
        }
    }

    private storeSession(payload: SupabaseAuthPayload): OwnitAuthSession {
        return this.toSession(this.storePayload(payload));
    }

    private storePayload(payload: SupabaseAuthPayload): StoredSupabaseSession {
        if (!payload.user?.id || !payload.access_token) {
            throw new Error('Supabase 인증 응답을 확인하지 못했습니다.');
        }
        const issuedAt = new Date().toISOString();
        const expiresAt = payload.expires_at || (payload.expires_in ? Math.floor(Date.now() / 1000) + Number(payload.expires_in) : undefined);
        const stored: StoredSupabaseSession = {
            ...payload,
            expires_at: expiresAt,
            issuedAt
        };
        this.writeStoredSession(stored);
        return stored;
    }

    private toSession(payload: StoredSupabaseSession): OwnitAuthSession {
        const user = payload.user;
        if (!user?.id) throw new Error('Supabase 사용자 정보를 확인하지 못했습니다.');
        const metadata = user.user_metadata || {};
        const name = (metadata.name || metadata.full_name || metadata.nickname || user.email || '사용자').trim();
        const nickname = (metadata.nickname || metadata.name || name).trim();
        const createdAt = user.created_at || payload.issuedAt || new Date().toISOString();
        return {
            provider: 'supabase',
            issuedAt: payload.issuedAt || new Date().toISOString(),
            accessToken: payload.access_token,
            user: {
                id: user.id,
                username: (metadata.username || user.email || user.id).trim(),
                email: (user.email || '').trim().toLowerCase(),
                authProvider: 'email',
                role: metadata.role === 'admin' ? 'admin' : 'owner',
                name,
                nickname,
                profileImage: metadata.profile_image || metadata.avatar_url || '',
                defaultFont: normalizeOwnitFontId(metadata.default_font),
                defaultTheme: metadata.default_theme || OWNIT_DEFAULT_THEME_ID,
                profileComplete: metadata.profile_complete !== false && Boolean(name || nickname),
                createdAt,
                updatedAt: user.updated_at || createdAt
            }
        };
    }

    private async authRequest<T = unknown>(path: string, options: { method: string; body?: any; accessToken?: string }): Promise<T> {
        const response = await fetch(`${this.config.url}/auth/v1/${path}`, {
            method: options.method,
            headers: {
                apikey: this.config.anonKey,
                Authorization: `Bearer ${options.accessToken || this.config.anonKey}`,
                'Content-Type': 'application/json'
            },
            body: options.body === undefined ? undefined : JSON.stringify(options.body)
        });

        if (!response.ok) {
            throw new Error(await this.readSupabaseError(response, 'Supabase 인증 요청이 실패했습니다.'));
        }

        if (response.status === 204) return undefined as T;
        return await response.json() as T;
    }

    private async readSupabaseError(response: Response, fallback: string): Promise<string> {
        try {
            const data = await response.json();
            return data.msg || data.message || data.error_description || data.error || fallback;
        } catch (error) {
            return fallback;
        }
    }

    private tryLocalSignIn(input: SignInInput): OwnitAuthSession | null {
        try {
            return this.localFallback.signIn(input);
        } catch (error) {
            return null;
        }
    }

    private readStoredSession(): StoredSupabaseSession | null {
        try {
            const raw = globalThis.localStorage?.getItem(SUPABASE_AUTH_SESSION_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as StoredSupabaseSession;
        } catch (error) {
            return null;
        }
    }

    private writeStoredSession(session: StoredSupabaseSession): void {
        globalThis.localStorage?.setItem(SUPABASE_AUTH_SESSION_KEY, JSON.stringify(session));
    }

    private clearStoredSession(): void {
        globalThis.localStorage?.removeItem(SUPABASE_AUTH_SESSION_KEY);
    }

    private isAdminIdentifier(identifier: string): boolean {
        const normalized = (identifier || '').trim().toLowerCase();
        return normalized === this.adminId || normalized === this.adminEmail;
    }

    private isAdminUser(user: Pick<OwnitAuthUser, 'id' | 'username' | 'email' | 'role'>): boolean {
        return user.role === 'admin';
    }
}
